import React, { useState, useEffect } from 'react';
import './Tips.css';

const Tips = () => {
  const [tips, setTips] = useState([]);
  const [filteredTips, setFilteredTips] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [savedTips, setSavedTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Categories of fitness tips
  const categories = [
    { id: 'all', name: 'All Tips' },
    { id: 'nutrition', name: 'Nutrition' },
    { id: 'cardio', name: 'Cardio' },
    { id: 'strength', name: 'Strength Training' },
    { id: 'recovery', name: 'Recovery' },
    { id: 'motivation', name: 'Motivation' }
  ];

  // Fetch tips on component mount
  useEffect(() => {
    const fetchTips = async () => {
      try {
        setLoading(true);
        // Simulate API call with timeout
        setTimeout(() => {
          // Sample tips data that would come from your Flask backend
          const sampleTips = [
            {
              id: 1,
              title: 'Pre-Workout Nutrition',
              content: 'Consume a balanced meal with carbs and protein 2-3 hours before workout. For quick energy, try a banana or small smoothie 30-60 minutes before exercise.',
              category: 'nutrition',
              difficulty: 'beginner',
              likes: 245
            },
            {
              id: 2,
              title: 'Proper Running Form',
              content: 'Land midfoot, keep your back straight, engage your core, and maintain a slight forward lean. Your arms should swing naturally at a 90-degree angle.',
              category: 'cardio',
              difficulty: 'intermediate',
              likes: 187
            },
            {
              id: 3,
              title: 'Progressive Overload',
              content: 'For continued strength gains, gradually increase weight, reps, or sets over time. Aim to increase by 5-10% when current weight becomes manageable.',
              category: 'strength',
              difficulty: 'advanced',
              likes: 322
            },
            {
              id: 4,
              title: 'Quality Sleep for Recovery',
              content: 'Aim for 7-9 hours of quality sleep. Your body repairs muscle tissue and replenishes energy stores during deep sleep cycles.',
              category: 'recovery',
              difficulty: 'beginner',
              likes: 278
            },
            {
              id: 5,
              title: 'Setting SMART Fitness Goals',
              content: 'Create Specific, Measurable, Achievable, Relevant, and Time-bound goals to stay motivated and track progress effectively.',
              category: 'motivation',
              difficulty: 'beginner',
              likes: 194
            },
            {
              id: 6,
              title: 'Post-Workout Protein Timing',
              content: 'Consume 20-30g of protein within 30 minutes after your workout to optimize muscle recovery and growth.',
              category: 'nutrition',
              difficulty: 'intermediate',
              likes: 231
            },
            {
              id: 7,
              title: 'HIIT Benefits',
              content: 'High-Intensity Interval Training burns more calories in less time and continues to burn calories hours after your workout due to the EPOC effect.',
              category: 'cardio',
              difficulty: 'advanced',
              likes: 312
            },
            {
              id: 8,
              title: 'Deload Weeks',
              content: 'Schedule a deload week every 4-6 weeks of intense training. Reduce volume and intensity by 40-60% to prevent injuries and allow for recovery.',
              category: 'recovery',
              difficulty: 'advanced',
              likes: 173
            }
          ];
          setTips(sampleTips);
          setFilteredTips(sampleTips);
          // Get saved tips from local storage
          const saved = JSON.parse(localStorage.getItem('savedTips')) || [];
          setSavedTips(saved);
          setLoading(false);
        }, 1000);
      } catch (err) {
        setError('Failed to fetch fitness tips. Please try again later.');
        setLoading(false);
      }
    };

    fetchTips();
  }, []);

  // Filter tips by category
  const filterTipsByCategory = (categoryId) => {
    setActiveCategory(categoryId);
    if (categoryId === 'all') {
      setFilteredTips(tips);
    } else {
      setFilteredTips(tips.filter(tip => tip.category === categoryId));
    }
  };

  // Toggle save/unsave tip
  const toggleSaveTip = (tipId) => {
    const isSaved = savedTips.includes(tipId);
    let updatedSavedTips;
    
    if (isSaved) {
      updatedSavedTips = savedTips.filter(id => id !== tipId);
    } else {
      updatedSavedTips = [...savedTips, tipId];
    }
    
    setSavedTips(updatedSavedTips);
    localStorage.setItem('savedTips', JSON.stringify(updatedSavedTips));
  };

  // Like a tip
  const likeTip = (tipId) => {
    const updatedTips = tips.map(tip => {
      if (tip.id === tipId) {
        return { ...tip, likes: tip.likes + 1 };
      }
      return tip;
    });
    
    setTips(updatedTips);
    setFilteredTips(
      activeCategory === 'all' 
        ? updatedTips 
        : updatedTips.filter(tip => tip.category === activeCategory)
    );
  };

  // Get difficulty badge class
  const getDifficultyClass = (difficulty) => {
    switch(difficulty) {
      case 'beginner':
        return 'badge-green';
      case 'intermediate':
        return 'badge-orange';
      case 'advanced':
        return 'badge-red';
      default:
        return 'badge-blue';
    }
  };

  // Get tip category icon
  const getCategoryIcon = (category) => {
    switch(category) {
      case 'nutrition':
        return '🍎';
      case 'cardio':
        return '🏃‍♂️';
      case 'strength':
        return '💪';
      case 'recovery':
        return '🛌';
      case 'motivation':
        return '🔥';
      default:
        return '📝';
    }
  };

  if (loading) {
    return <div className="tips-loading">Loading fitness tips...</div>;
  }

  if (error) {
    return <div className="tips-error">{error}</div>;
  }

  return (
    <div className="tips-container">
      <div className="tips-header">
        <h2>Fitness Tips & Recommendations</h2>
        <p className="tips-subtitle">Expert advice to enhance your fitness journey</p>
      </div>

      <div className="tips-dashboard">
        {/* Category filter tabs */}
        <div className="tips-categories">
          {categories.map(category => (
            <button
              key={category.id}
              className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => filterTipsByCategory(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Featured tip */}
        {filteredTips.length > 0 && (
          <div className="featured-tip">
            <div className="featured-content">
              <div className="featured-header">
                <span className="featured-label">Featured Tip</span>
                <span className={`tip-badge ${getDifficultyClass(filteredTips[0].difficulty)}`}>
                  {filteredTips[0].difficulty}
                </span>
              </div>
              <h3>{getCategoryIcon(filteredTips[0].category)} {filteredTips[0].title}</h3>
              <p>{filteredTips[0].content}</p>
              <div className="tip-actions">
                <button 
                  className={`btn-save ${savedTips.includes(filteredTips[0].id) ? 'saved' : ''}`}
                  onClick={() => toggleSaveTip(filteredTips[0].id)}
                >
                  {savedTips.includes(filteredTips[0].id) ? 'Saved ✓' : 'Save Tip'}
                </button>
                <button className="btn-like" onClick={() => likeTip(filteredTips[0].id)}>
                  👍 {filteredTips[0].likes}
                </button>
              </div>
            </div>
            <div className="featured-image">
              <div className="tip-illustration"></div>
            </div>
          </div>
        )}

        {/* Tips grid */}
        {filteredTips.length > 1 ? (
          <div className="tips-grid">
            {filteredTips.slice(1).map(tip => (
              <div key={tip.id} className="tip-card">
                <div className="tip-header">
                  <span className={`tip-badge ${getDifficultyClass(tip.difficulty)}`}>
                    {tip.difficulty}
                  </span>
                  <button 
                    className={`btn-bookmark ${savedTips.includes(tip.id) ? 'bookmarked' : ''}`}
                    onClick={() => toggleSaveTip(tip.id)}
                  >
                    {savedTips.includes(tip.id) ? '★' : '☆'}
                  </button>
                </div>
                <h4>{getCategoryIcon(tip.category)} {tip.title}</h4>
                <p className="tip-content">{tip.content}</p>
                <div className="tip-footer">
                  <span className="tip-category">{tip.category}</span>
                  <button className="btn-like-small" onClick={() => likeTip(tip.id)}>
                    👍 {tip.likes}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : filteredTips.length === 0 ? (
          <div className="no-tips">
            <p>No tips found in this category. Check back later!</p>
          </div>
        ) : null}

        {/* Daily tip section */}
        <div className="daily-tip-section">
          <div className="daily-tip-header">
            <h3>Tip of the Day</h3>
            <span className="refresh-tip">Refresh</span>
          </div>
          <div className="daily-tip-content">
            <div className="daily-tip-icon">💡</div>
            <div className="daily-tip-text">
              <p>"Stay consistent with your workouts. Even a short 15-minute session is better than skipping exercise altogether. Consistency builds habits that last."</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tips;